import { default as React, useState, useCallback } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Box,
  Grid,
  Typography,
  InputLabel,
} from '@material-ui/core';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

interface PythonServiceOptions {
  python_version: string;
  framework: string;
  package_manager: string;
  test_framework: string;
  database: string;
  async_support: boolean;
  include_docker: boolean;
}

const DEFAULT_OPTIONS: PythonServiceOptions = {
  python_version: '3.12',
  framework: 'FastAPI',
  package_manager: 'uv',
  test_framework: 'pytest',
  database: 'None',
  async_support: true,
  include_docker: true,
};

const PYTHON_VERSIONS = ['3.10', '3.11', '3.12'];
const FRAMEWORKS = ['FastAPI', 'Flask', 'Django'];
const PACKAGE_MANAGERS = ['uv', 'pip', 'poetry'];
const TEST_FRAMEWORKS = ['pytest', 'unittest'];
const DATABASES = ['None', 'PostgreSQL', 'MySQL', 'MongoDB'];

const FRAMEWORK_DESCRIPTIONS: Record<string, string> = {
  FastAPI:
    'Modern, high-performance web framework with automatic API documentation',
  Flask: 'Lightweight WSGI web framework, flexible and easy to extend',
  Django: 'High-level web framework with batteries included (ORM, admin, auth)',
};

const DATABASE_DESCRIPTIONS: Record<string, string> = {
  None: 'No database integration',
  PostgreSQL: 'Advanced open-source relational database',
  MySQL: 'Popular open-source relational database',
  MongoDB: 'NoSQL document database',
};

export const PythonServiceOptionsField = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
}: FieldExtensionComponentProps<Record<string, any>>) => {
  const [options, setOptions] = useState<PythonServiceOptions>(
    (formData as PythonServiceOptions) || DEFAULT_OPTIONS,
  );

  const hasErrors = rawErrors && rawErrors.length > 0;

  const handleChange = useCallback(
    (field: keyof PythonServiceOptions, value: any) => {
      const newOptions = { ...options, [field]: value };
      setOptions(newOptions);
      onChange(newOptions);
    },
    [options, onChange],
  );

  const handleSelectChange = useCallback(
    (field: keyof PythonServiceOptions) => (event: any) => {
      handleChange(field, event.target.value);
    },
    [handleChange],
  );

  const handleSwitchChange = useCallback(
    (field: keyof PythonServiceOptions) => (event: any) => {
      handleChange(field, event.target.checked);
    },
    [handleChange],
  );

  return (
    <Box style={{ marginTop: 16, marginBottom: 16 }}>
      <Typography
        variant="h6"
        gutterBottom
        style={{ fontWeight: 600, marginBottom: 24 }}
      >
        {schema?.title || 'Python Service Configuration'}
      </Typography>

      <Grid container spacing={3}>
        {/* Python Version */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required={required} error={hasErrors}>
            <InputLabel id="python-version-label">Python Version</InputLabel>
            <Select
              labelId="python-version-label"
              value={options.python_version}
              onChange={handleSelectChange('python_version')}
              label="Python Version"
            >
              {PYTHON_VERSIONS.map(version => (
                <MenuItem key={version} value={version}>
                  Python {version}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Select the Python runtime version</FormHelperText>
          </FormControl>
        </Grid>

        {/* Framework */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required={required} error={hasErrors}>
            <InputLabel id="framework-label">Framework</InputLabel>
            <Select
              labelId="framework-label"
              value={options.framework}
              onChange={handleSelectChange('framework')}
              label="Framework"
            >
              {FRAMEWORKS.map(framework => (
                <MenuItem key={framework} value={framework}>
                  {framework}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {FRAMEWORK_DESCRIPTIONS[options.framework]}
            </FormHelperText>
          </FormControl>
        </Grid>

        {/* Package Manager */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required={required} error={hasErrors}>
            <InputLabel id="package-manager-label">Package Manager</InputLabel>
            <Select
              labelId="package-manager-label"
              value={options.package_manager}
              onChange={handleSelectChange('package_manager')}
              label="Package Manager"
            >
              {PACKAGE_MANAGERS.map(manager => (
                <MenuItem key={manager} value={manager}>
                  {manager}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {options.package_manager === 'uv' &&
                'Ultra-fast Python package installer and resolver'}
              {options.package_manager === 'pip' &&
                'Standard Python package installer'}
              {options.package_manager === 'poetry' &&
                'Dependency management and packaging tool'}
            </FormHelperText>
          </FormControl>
        </Grid>

        {/* Test Framework */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required={required} error={hasErrors}>
            <InputLabel id="test-framework-label">Test Framework</InputLabel>
            <Select
              labelId="test-framework-label"
              value={options.test_framework}
              onChange={handleSelectChange('test_framework')}
              label="Test Framework"
            >
              {TEST_FRAMEWORKS.map(framework => (
                <MenuItem key={framework} value={framework}>
                  {framework}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {options.test_framework === 'pytest' &&
                'Full-featured testing framework with plugins'}
              {options.test_framework === 'unittest' &&
                'Built-in Python testing framework'}
            </FormHelperText>
          </FormControl>
        </Grid>

        {/* Database */}
        <Grid item xs={12}>
          <FormControl fullWidth required={required} error={hasErrors}>
            <InputLabel id="database-label">Database</InputLabel>
            <Select
              labelId="database-label"
              value={options.database}
              onChange={handleSelectChange('database')}
              label="Database"
            >
              {DATABASES.map(database => (
                <MenuItem key={database} value={database}>
                  {database}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {DATABASE_DESCRIPTIONS[options.database]}
            </FormHelperText>
          </FormControl>
        </Grid>

        {/* Switches */}
        <Grid item xs={12}>
          <Box style={{ display: 'flex', flexDirection: 'column' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={options.async_support}
                  onChange={handleSwitchChange('async_support')}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" style={{ fontWeight: 500 }}>
                    Async Support
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Enable async/await patterns for concurrent operations
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={options.include_docker}
                  onChange={handleSwitchChange('include_docker')}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" style={{ fontWeight: 500 }}>
                    Include Docker
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Generate Dockerfile and docker-compose.yml for
                    containerization
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Grid>
      </Grid>

      {hasErrors && (
        <FormHelperText error style={{ marginTop: 16 }}>
          {rawErrors?.join(', ')}
        </FormHelperText>
      )}

      {schema?.description && !hasErrors && (
        <FormHelperText style={{ marginTop: 16 }}>
          {schema.description}
        </FormHelperText>
      )}
    </Box>
  );
};

export const PythonServiceOptionsExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'PythonServiceOptions',
    component: PythonServiceOptionsField,
  }),
);
